import { motion } from "framer-motion";
import {
    Droplet,
    Eye,
    ArrowDownToLine,
    Timer,
    ScanLine,
    Zap,
    CheckCircle2,
    Package,
    Thermometer,
    ShieldCheck,
} from "lucide-react";
import { FloatingCube } from "@/three/ProductStage";
import { useViewEvent } from "@/lib/analytics";

const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.7 },
};

const STEPS = [
    { n: "1", title: "RECOLECTE", text: "Recolecte la muestra de saliva utilizando el hisopo.", icon: Droplet },
    { n: "2", title: "VERIFIQUE", text: "Observe el indicador de saturación.", icon: Eye },
    { n: "3", title: "INSERTE", text: "Inserte el colector en el dispositivo.", icon: ArrowDownToLine },
    { n: "4", title: "ESPERE", text: "Permita que el test procese la muestra.", icon: Timer },
    { n: "5", title: "LEA", text: "Interprete visualmente el resultado.", icon: ScanLine },
];

export function Procedure() {
    const viewRef = useViewEvent("view_procedure");
    return (
        <section
            id="como-funciona"
            ref={viewRef}
            data-testid="procedure-section"
            className="relative bg-white py-28 md:py-40"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-2xl">
                    <p className="mb-5 text-[11px] font-bold tracking-[0.28em] text-[#059669]">PROCEDIMIENTO</p>
                    <h2 className="font-display text-4xl font-extrabold tracking-tighter text-[#0A192F] sm:text-5xl lg:text-6xl">
                        SIMPLE DE PRINCIPIO <span className="text-[#57A632]">A FIN.</span>
                    </h2>
                </motion.div>

                <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                    {STEPS.map((s, i) => (
                        <motion.div
                            key={s.n}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            data-testid={`procedure-step-${s.n}`}
                            className="group relative rounded-3xl border border-slate-100 bg-white p-7 shadow-[0_4px_20px_-4px_rgba(10,25,47,0.05)] transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-1.5 hover:border-[#059669]/30 hover:shadow-[0_16px_40px_-12px_rgba(10,25,47,0.14)]"
                        >
                            <span className="font-display text-5xl font-extrabold text-[#57A632]">
                                {s.n}
                            </span>
                            <div className="mt-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0A192F] text-white transition-colors duration-300 group-hover:bg-[#059669]">
                                <s.icon size={19} />
                            </div>
                            <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-[#0A192F]">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.text}</p>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    {...fadeUp}
                    className="mt-20 grid items-center gap-10 rounded-3xl border border-slate-100 bg-[#F8FAFC] p-8 md:p-12 lg:grid-cols-2"
                >
                    <div>
                        <p className="mb-4 text-[11px] font-bold tracking-[0.28em] text-[#059669]">
                            COMPONENTES DEL DISPOSITIVO
                        </p>
                        <h3 className="font-display text-2xl font-bold tracking-tight text-[#0A192F] sm:text-3xl">
                            Dispositivo de prueba + hisopo de recolección
                        </h3>
                        <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">
                            Cuerpo del Cube con etiqueta de seguridad desprendible, zona de identificación y hisopo con
                            mango de bloqueo, ventana indicadora y tira de saturación.
                        </p>
                    </div>
                    <div className="flex justify-center">
                        <img
                            src="/assets/cube-diagram.webp"
                            alt="Componentes del LYHER Oral Fluid Drug Test Cube: dispositivo de prueba e hisopo recolector con indicador de saturación"
                            width="1000"
                            height="1000"
                            className="h-auto max-h-[380px] w-auto rounded-2xl object-contain"
                            data-testid="procedure-diagram"
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

export function Fda() {
    const viewRef = useViewEvent("view_fda");
    return (
        <section
            id="fda"
            ref={viewRef}
            data-testid="fda-section"
            className="grain relative overflow-hidden bg-[#0A192F] py-28 md:py-40"
        >
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:px-10">
                <motion.div {...fadeUp}>
                    <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md">
                        <ShieldCheck size={15} className="text-[#57A632]" />
                        <span className="text-xs font-bold tracking-[0.18em] text-white/80">
                            AUTORIZACIÓN REGULATORIA
                        </span>
                    </div>
                    <h2 className="font-display font-extrabold leading-[0.9] tracking-tighter text-white" data-testid="fda-headline">
                        <span className="block text-7xl sm:text-8xl lg:text-9xl">FDA</span>
                        <span className="block text-5xl text-[#57A632] sm:text-6xl lg:text-7xl">510(k)</span>
                    </h2>
                    <p className="mt-8 max-w-md text-base font-semibold text-white/90 md:text-lg">
                        LYHER® Oral Fluid Multi-Drug Test Kit (Cube)
                    </p>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-white/50 sm:text-base">
                        Tecnología respaldada por autorización FDA 510(k).
                    </p>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.9 }}
                    className="relative h-[420px] md:h-[520px]"
                >
                    <FloatingCube className="h-full w-full" />
                </motion.div>
            </div>
        </section>
    );
}

const DRUGS = [
    { code: "OPI", name: "Opiáceos", cutoff: 40 },
    { code: "COC", name: "Cocaína", cutoff: 20 },
    { code: "AMP", name: "Anfetamina", cutoff: 50 },
    { code: "THC", name: "Cannabis", cutoff: 40 },
    { code: "MET", name: "Metanfetamina", cutoff: 50 },
    { code: "PCP", name: "Fenciclidina", cutoff: 10 },
];

export function Drugs() {
    const viewRef = useViewEvent("view_analytes");
    return (
        <section id="deteccion" ref={viewRef} data-testid="drugs-section" className="relative bg-[#F8FAFC] py-28 md:py-40">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeUp} className="flex flex-wrap items-end justify-between gap-6">
                    <div className="max-w-xl">
                        <p className="mb-5 text-[11px] font-bold tracking-[0.28em] text-[#059669]">ANALITOS</p>
                        <h2 className="font-display text-4xl font-extrabold tracking-tighter text-[#0A192F] sm:text-5xl lg:text-6xl">
                            DETECCIÓN <span className="text-[#57A632]">MULTI-DROGA</span>
                        </h2>
                    </div>
                    <p className="max-w-xs text-sm leading-relaxed text-slate-500">
                        Seis analitos en una sola muestra de fluido oral, con cut-offs definidos por el fabricante.
                    </p>
                </motion.div>

                <div className="mt-16 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                    {DRUGS.map((d, i) => (
                        <motion.div
                            key={d.code}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: i * 0.07 }}
                            data-testid={`drug-card-${d.code.toLowerCase()}`}
                            className="group relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1.5 hover:border-[#059669] hover:shadow-[0_0_0_1px_#059669,0_18px_40px_-14px_rgba(5,150,105,0.35)]"
                        >
                            <div className="absolute right-4 top-4 h-2 w-2 rounded-full bg-slate-200 transition-colors duration-300 group-hover:bg-[#059669] group-hover:shadow-[0_0_10px_rgba(5,150,105,0.7)]" />
                            <p className="font-display text-3xl font-extrabold tracking-tight text-[#0A192F] transition-colors duration-300 group-hover:text-[#059669]">
                                {d.code}
                            </p>
                            <p className="mt-1 text-xs font-semibold text-slate-500">{d.name}</p>
                            <div className="mt-6 border-t border-slate-100 pt-3">
                                <p className="text-[10px] font-bold tracking-[0.18em] text-slate-400">CUT-OFF</p>
                                <p className="mt-1 font-display text-lg font-bold text-[#0A192F]">
                                    {d.cutoff} <span className="text-xs font-semibold text-slate-400">ng/mL</span>
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const BENEFITS = [
    { title: "RÁPIDO", text: "Resultados visuales en minutos.", icon: Zap, span: "lg:col-span-7" },
    { title: "SIMPLE", text: "Procedimiento fácil de ejecutar.", icon: CheckCircle2, span: "lg:col-span-5" },
    { title: "VISUAL", text: "Interpretación directa del dispositivo.", icon: Eye, span: "lg:col-span-4" },
    { title: "PORTÁTIL", text: "No requiere grandes equipos para el screening visual.", icon: Package, span: "lg:col-span-4" },
    { title: "TEMPERATURA AMBIENTE", text: "Almacenamiento según especificaciones del fabricante.", icon: Thermometer, span: "lg:col-span-4" },
];

export function Benefits() {
    return (
        <section id="beneficios" data-testid="benefits-section" className="relative bg-white py-28 md:py-40">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-3xl">
                    <p className="mb-5 text-[11px] font-bold tracking-[0.28em] text-[#059669]">BENEFICIOS</p>
                    <h2 className="font-display text-4xl font-extrabold tracking-tighter text-[#0A192F] sm:text-5xl lg:text-6xl">
                        TECNOLOGÍA DISEÑADA PARA <span className="text-[#57A632]">OPERAR RÁPIDO.</span>
                    </h2>
                </motion.div>

                <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-12">
                    {BENEFITS.map((b, i) => (
                        <motion.div
                            key={b.title}
                            initial={{ opacity: 0, y: 36 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.08 }}
                            data-testid={`benefit-${b.title.toLowerCase().replace(/\s/g, "-")}`}
                            className={`group rounded-3xl border border-slate-100 bg-[#F8FAFC] p-8 transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_44px_-14px_rgba(10,25,47,0.14)] md:p-10 ${b.span}`}
                        >
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0A192F] text-white transition-colors duration-300 group-hover:bg-[#059669]">
                                <b.icon size={20} />
                            </div>
                            <h3 className="mt-6 font-display text-xl font-bold tracking-[0.06em] text-[#0A192F]">
                                {b.title}
                            </h3>
                            <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-500 sm:text-base">{b.text}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const INDUSTRIES = [
    { name: "Minería", img: "https://images.unsplash.com/photo-1523848309072-c199db53f137?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBvcGVyYXRpb25zfGVufDB8fHx8MTc4NjkzODA4N3ww&ixlib=rb-4.1.0&q=85" },
    { name: "Transporte", img: "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwdHJ1Y2tpbmd8ZW58MHx8fHwxNzg2OTM4MDg3fDA&ixlib=rb-4.1.0&q=85" },
    { name: "Construcción", img: "https://images.pexels.com/photos/3818947/pexels-photo-3818947.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { name: "Logística", img: "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwdHJ1Y2tpbmd8ZW58MHx8fHwxNzg2OTM4MDg3fDA&ixlib=rb-4.1.0&q=85" },
    { name: "Industria", img: "https://images.unsplash.com/photo-1523848309072-c199db53f137?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBvcGVyYXRpb25zfGVufDB8fHx8MTc4NjkzODA4N3ww&ixlib=rb-4.1.0&q=85" },
    { name: "Salud Ocupacional", img: "https://images.pexels.com/photos/8851456/pexels-photo-8851456.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
];

export function Industries() {
    return (
        <section id="industrias" data-testid="industries-section" className="relative bg-[#0A192F] py-28 md:py-40">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeUp} className="max-w-3xl">
                    <p className="mb-5 text-[11px] font-bold tracking-[0.28em] text-[#57A632]">USOS / INDUSTRIAS</p>
                    <h2 className="font-display text-4xl font-extrabold tracking-tighter text-white sm:text-5xl lg:text-6xl">
                        PARA ENTORNOS DONDE LA <span className="text-[#57A632]">PREVENCIÓN IMPORTA.</span>
                    </h2>
                </motion.div>

                <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {INDUSTRIES.map((ind, i) => (
                        <motion.div
                            key={ind.name}
                            initial={{ opacity: 0, y: 36 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                            data-testid={`industry-${ind.name.toLowerCase().replace(/\s/g, "-")}`}
                            className="group relative h-64 overflow-hidden rounded-3xl md:h-72"
                        >
                            <img
                                src={ind.img}
                                alt={ind.name}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-[#0A192F]/45 transition-colors duration-500 group-hover:bg-[#0A192F]/25" />
                            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-6">
                                <h3 className="font-display text-xl font-bold tracking-tight text-white">{ind.name}</h3>
                                <span className="h-2 w-2 rounded-full bg-[#57A632] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
